import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { rpc, useAction, useHasRole, useIssuers, useOrg, type IssuerRow } from "@vixio/data";
import { Button, Checkbox, DataTable, Dialog, DocInput, PageHeader, Select, StatusPill, TextInput, formatDoc } from "@vixio/ui";

export const TAX_REGIMES = [
  { value: "mei", label: "MEI" },
  { value: "simples", label: "Simples Nacional" },
  { value: "presumido", label: "Lucro Presumido" },
  { value: "real", label: "Lucro Real" },
] as const;

const regimeLabel = (r: string) => TAX_REGIMES.find((x) => x.value === r)?.label ?? r;

export function IssuersPage() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const issuers = useIssuers(org.id);
  const [editing, setEditing] = useState<IssuerRow | "new" | null>(null);
  return (
    <div className="vx-content">
      <PageHeader
        title="Emissores (CNPJs)"
        description="CNPJs que emitem nota, cobram e contabilizam separados (ex.: Vixio e InPES). Financeiro, cobrança e fiscal apontam para cá."
        actions={
          isAdmin ? (
            <Button variant="primary" icon={<Plus />} onClick={() => setEditing("new")}>
              Novo emissor
            </Button>
          ) : null
        }
      />
      <DataTable<IssuerRow>
        caption="Emissores"
        data={issuers.data ?? []}
        loading={issuers.isLoading}
        searchKeys={["legal_name", "trade_name", "cnpj"]}
        searchPlaceholder="Buscar por razão social, nome ou CNPJ"
        rowKey={(i) => i.id}
        onRowClick={isAdmin ? (i) => setEditing(i) : undefined}
        exportName="emissores"
        empty="Nenhum CNPJ emissor cadastrado. Cadastre ao menos um para cobrar e emitir notas."
        columns={[
          {
            key: "legal_name",
            header: "Razão social",
            sortable: true,
            cell: (i) => (
              <span className="vx-table__primary">
                {i.legal_name}
                {i.trade_name ? <span className="vx-table__sub">{i.trade_name}</span> : null}
              </span>
            ),
          },
          { key: "cnpj", header: "CNPJ", kind: "doc" },
          { key: "tax_regime", header: "Regime", cell: (i) => regimeLabel(i.tax_regime), csv: (i) => regimeLabel(i.tax_regime) },
          { key: "municipal_registration", header: "Inscrição municipal" },
          { key: "municipality_ibge", header: "Município (IBGE)", cell: (i) => <span className="mono">{i.municipality_ibge ?? "-"}</span> },
          { key: "active", header: "Situação", cell: (i) => (i.active ? <StatusPill tone="ok">ativo</StatusPill> : <StatusPill>inativo</StatusPill>), csv: (i) => (i.active ? "ativo" : "inativo") },
        ]}
      />
      <IssuerDialog issuer={editing === "new" ? null : editing} open={editing !== null} onOpenChange={(o) => !o && setEditing(null)} />
    </div>
  );
}

function IssuerDialog({ issuer, open, onOpenChange }: { issuer: IssuerRow | null; open: boolean; onOpenChange: (o: boolean) => void }) {
  const org = useOrg();
  const [legal, setLegal] = useState("");
  const [trade, setTrade] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [cnpjOk, setCnpjOk] = useState(false);
  const [regime, setRegime] = useState<string>("simples");
  const [im, setIm] = useState("");
  const [ibge, setIbge] = useState("");
  const [active, setActive] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLegal(issuer?.legal_name ?? "");
    setTrade(issuer?.trade_name ?? "");
    setCnpj(issuer?.cnpj ?? "");
    setCnpjOk(Boolean(issuer));
    setRegime(issuer?.tax_regime ?? "simples");
    setIm(issuer?.municipal_registration ?? "");
    setIbge(issuer?.municipality_ibge ?? "");
    setActive(issuer?.active ?? true);
    setSubmitted(false);
  }, [open, issuer]);

  const save = useAction(
    () =>
      rpc("core", "upsert_issuer", {
        p_org: org.id,
        p_legal_name: legal.trim(),
        p_cnpj: cnpj,
        p_tax_regime: regime,
        p_trade_name: trade.trim() || null,
        p_municipal_registration: im.trim() || null,
        p_municipality_ibge: ibge.trim() || null,
        p_active: active,
      }),
    { invalidate: [["core", "issuers"]], success: "Emissor salvo", onSuccess: () => onOpenChange(false) },
  );
  const ibgeErr = submitted && ibge && !/^\d{7}$/.test(ibge) ? "Código IBGE tem 7 dígitos." : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={issuer ? `Editar ${formatDoc(issuer.cnpj)}` : "Novo emissor"}
      onSubmit={() => {
        setSubmitted(true);
        if (!legal.trim() || !cnpjOk || (ibge && !/^\d{7}$/.test(ibge))) return;
        save.mutate(undefined);
      }}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="vx-form-grid">
        <TextInput className="span-2" label="Razão social" value={legal} onChange={setLegal} required error={submitted && !legal.trim() ? "Obrigatório." : null} />
        <TextInput label="Nome fantasia" value={trade} onChange={setTrade} />
        <DocInput
          label="CNPJ"
          kind="cnpj"
          value={cnpj}
          disabled={Boolean(issuer)}
          hint={issuer ? "O CNPJ identifica o emissor e não muda." : "Aceita o CNPJ alfanumérico."}
          onChange={(v, ok) => {
            setCnpj(v);
            setCnpjOk(ok);
          }}
          error={submitted && !cnpjOk ? "Informe um CNPJ válido." : null}
          required
        />
        <Select label="Regime tributário" value={regime} onChange={setRegime} options={TAX_REGIMES.map((r) => ({ value: r.value, label: r.label }))} />
        <TextInput label="Inscrição municipal" value={im} onChange={setIm} />
        <TextInput label="Município (código IBGE)" value={ibge} onChange={(v) => setIbge(v.replace(/\D/g, "").slice(0, 7))} inputMode="numeric" error={ibgeErr} hint="Ex.: 2927408 (Salvador)." />
        <div className="span-2">
          <Checkbox label="Ativo para emitir e cobrar" checked={active} onChange={setActive} />
        </div>
      </div>
    </Dialog>
  );
}
